import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

export interface InviteModalData {
  inviteCode?: string;
  tenantId?: string;
  isPersonalTenant?: boolean;
  bossSnid?: string;
  tenantType?: string;
  hasDeviceNetworkAccessControlCapability?: boolean;
  hasClientAccessControlCapability?: boolean;
}

export interface TenantInvitationVO {
  code: string;
  type?: string;
  text?: string;
  expireTime?: number;
  mobileAccessEnabled?: boolean;
  expireDays?: number;
  maxUsageCount?: number;
  usedCount?: number;
  warnCode?: string;
  warnMessage?: string;
}

@Component({
  selector: 'app-invite-code-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzButtonModule,
    NzInputModule,
    NzIconModule,
    NzDividerModule,
    NzSelectModule,
    NzSpinModule,
    TestIdDirective,
    FormsModule,
    I18nPipe
  ],
  template: `
    <div class="invite-modal-container" [testId]="'invite-code-modal-container'">
      <div *ngIf="!isPersonalTenant" class="invite-intro" [testId]="'invite-modal-description'">
        {{ 'tenant.invite.subtitle' | i18n }}
      </div>

      <!-- Configuration Section: Shown for Team tenants when inviteCode is not yet generated -->
      <div *ngIf="!inviteCode && !isPersonalTenant" class="invite-config-section" [testId]="'invite-config-section'">
        <div *ngIf="hasClientAccessControlCapability" class="section-group" [testId]="'invite-policy-group'">
          <div class="section-title" [testId]="'invite-policy-title'">
            {{ 'tenant.invite.app-access-title' | i18n }}
          </div>
          <div class="policy-cards" [testId]="'invite-policy-cards'">
            <div
              class="policy-card"
              [class.active]="mobileAccessEnabled === true"
              (click)="setMobileAccess(true)"
              [testId]="'invite-policy-allow-btn'"
            >
              <div class="policy-card-header">
                <span class="policy-card-title">{{ 'tenant.invite.allow-app-title' | i18n }}</span>
              </div>
              <div class="policy-card-desc">
                {{ 'tenant.invite.allow-app-desc' | i18n }}
              </div>
            </div>

            <div
              class="policy-card"
              [class.active]="mobileAccessEnabled === false"
              (click)="setMobileAccess(false)"
              [testId]="'invite-policy-deny-btn'"
            >
              <div class="policy-card-header">
                <span class="policy-card-title">{{ 'tenant.invite.desktop-only-title' | i18n }}</span>
              </div>
              <div class="policy-card-desc">
                {{ 'tenant.invite.desktop-only-desc' | i18n }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-row" [testId]="'invite-form-row'">
          <div class="form-col" [testId]="'invite-expire-col'">
            <label class="form-label" [testId]="'invite-expire-label'">{{ 'tenant.invite.validity-period' | i18n }}</label>
            <nz-select [(ngModel)]="expireDays" class="full-width" [testId]="'invite-expire-days-select'">
              <nz-option [nzValue]="1" [nzLabel]="'tenant.invite.validity-1-day' | i18n" />
              <nz-option [nzValue]="7" [nzLabel]="'tenant.invite.validity-7-days' | i18n" />
              <nz-option [nzValue]="30" [nzLabel]="'tenant.invite.validity-30-days' | i18n" />
            </nz-select>
          </div>

          <div class="form-col" [testId]="'invite-usage-col'">
            <label class="form-label" [testId]="'invite-usage-label'">{{ 'tenant.invite.usage-limit' | i18n }}</label>
            <nz-select [(ngModel)]="maxUsageCount" class="full-width" [testId]="'invite-max-usage-select'">
              <nz-option [nzValue]="1" [nzLabel]="'tenant.invite.usage-1-time' | i18n" />
              <nz-option [nzValue]="5" [nzLabel]="'tenant.invite.usage-5-times' | i18n" />
              <nz-option [nzValue]="-1" [nzLabel]="'tenant.invite.usage-unlimited' | i18n" />
            </nz-select>
          </div>
        </div>

        <div class="action-footer" [testId]="'invite-action-footer'">
          <button
            nz-button
            nzType="primary"
            class="generate-button"
            [nzLoading]="isGenerating"
            (click)="generateInviteCode()"
            [testId]="'generate-invite-code-btn'"
          >
            {{ 'tenant.invite.generate-button' | i18n }}
          </button>
        </div>
      </div>

      <!-- Loading Section: Shown for personal tenants if generating code -->
      <div *ngIf="!inviteCode && isPersonalTenant" class="personal-loading-section" [testId]="'personal-invite-loading'">
        <nz-spin nzSimple />
      </div>

      <!-- Result Section: Shown when inviteCode is generated or provided -->
      <div *ngIf="inviteCode" class="invite-result-section" [testId]="'invite-result-section'">
        <div class="invite-code-section" [testId]="'invite-code-section'">
          <div class="invite-code-title" [testId]="'invite-code-title'">
            <span [testId]="'invite-code-instruction'">{{ 'tenant.invite.copy-invite-code-instruction' | i18n }}</span>
          </div>
          <div class="code-display" [testId]="'code-display'">
            <input nz-input [(ngModel)]="inviteCode" id="inviteCodeInput" readonly [testId]="'invite-code-input'" />
            <button nz-button [nzType]="'default'" (click)="copyInviteCode()" class="copy-button" [testId]="'copy-invite-code-button'">
              <i nz-icon nzType="copy" nzTheme="outline" [testId]="'copy-icon'"></i>
              {{ 'tenant.invite.copy' | i18n }}
            </button>
          </div>
          <div *ngIf="!isPersonalTenant" class="meta-summary" [testId]="'invite-code-meta'">
            {{ getMetaSummary() }}
          </div>
        </div>

        <div class="share-section" [testId]="'share-section'">
          <div class="share-options" [testId]="'share-options'">
            <button nz-button (click)="shareVia('line')" class="share-button line" [testId]="'share-button-line'">
              <img src="assets/images/channels/icon-im-line.svg" alt="LINE" [testId]="'share-icon-line'" />
              LINE
            </button>
            <button nz-button (click)="shareVia('slack')" class="share-button slack" [testId]="'share-button-slack'">
              <img src="assets/images/channels/icon-im-slack.svg" alt="Slack" [testId]="'share-icon-slack'" />
              Slack
            </button>
            <button nz-button (click)="shareVia('teams')" class="share-button teams" [testId]="'share-button-teams'">
              <img src="assets/images/channels/icon-im-teams.svg" alt="Teams" [testId]="'share-icon-teams'" />
              Teams
            </button>
            <button nz-button (click)="shareVia('gchat')" class="share-button gchat" [testId]="'share-button-gchat'">
              <img src="assets/images/channels/icon-im-google-chat.svg" alt="Google Chat" [testId]="'share-icon-gchat'" />
              Google Chat
            </button>
          </div>
        </div>

        <div *ngIf="!isPersonalTenant" class="result-footer" [testId]="'invite-result-footer'">
          <button nz-button nzType="link" (click)="resetConfig()" class="reset-link" [testId]="'reset-invite-config-btn'">
            <i nz-icon nzType="redo"></i>
            {{ 'tenant.invite.reset-config' | i18n }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .invite-modal-container {
        padding: 0;
        color: #1f2937;
      }

      .invite-intro {
        margin-bottom: 20px;
        color: #667085;
        font-size: 14px;
        line-height: 22px;
      }

      .invite-config-section {
        padding: 24px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04);
      }

      .section-group {
        margin-bottom: 28px;
      }

      .section-title {
        margin-bottom: 14px;
        color: #111827;
        font-size: 15px;
        font-weight: 600;
        line-height: 22px;
      }

      .policy-cards {
        display: flex;
        gap: 16px;

        @media (max-width: 576px) {
          flex-direction: column;
        }
      }

      .policy-card {
        flex: 1;
        min-width: 0;
        padding: 16px 20px;
        border: 1px solid #d9dfe8;
        border-radius: 8px;
        cursor: pointer;
        background: #ffffff;
        min-height: 82px;
        transition:
          border-color 0.16s ease,
          background-color 0.16s ease,
          box-shadow 0.16s ease;

        &:hover {
          border-color: #91caff;
          background: #f8fbff;
        }

        &.active {
          border-color: #1890ff;
          background: #f0f7ff;
          box-shadow: 0 0 0 1px #1890ff;

          .policy-card-title {
            color: #1890ff;
          }
        }
      }

      .policy-card-header {
        display: flex;
        align-items: center;
        min-height: 22px;
      }

      .policy-card-title {
        color: #1f2937;
        font-size: 15px;
        font-weight: 600;
        line-height: 22px;
        transition: color 0.16s ease;
      }

      .policy-card-desc {
        margin-top: 6px;
        color: #4b5563;
        font-size: 13px;
        line-height: 20px;
      }

      .form-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 24px;
        margin-bottom: 32px;

        @media (max-width: 576px) {
          grid-template-columns: minmax(0, 1fr);
          gap: 16px;
        }
      }

      .form-col {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;

        .form-label {
          flex: 0 0 auto;
          min-width: 68px;
          margin: 0;
          color: #374151;
          font-size: 14px;
          font-weight: 500;
          line-height: 22px;
          white-space: nowrap;
        }

        .full-width {
          flex: 1;
          width: 100%;
          min-width: 140px;

          ::ng-deep .ant-select-selector {
            height: 38px !important;
            min-height: 38px !important;
            padding: 0 12px !important;
            border-radius: 6px !important;
          }

          ::ng-deep .ant-select-selection-item {
            display: flex;
            align-items: center;
            line-height: 36px;
            font-size: 14px;
          }
        }
      }

      .action-footer {
        display: flex;
        justify-content: flex-start;
        padding-top: 4px;

        .generate-button {
          min-width: 120px;
          height: 40px;
          padding: 0 24px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          box-shadow: none;
        }
      }

      .invite-result-section {
        .invite-code-section {
          .invite-code-title {
            margin-bottom: 8px;
          }
          span {
            margin-bottom: 16px;
            color: #1f2937;
            font-size: 15px;
            font-weight: 600;
            line-height: 24px;
          }
        }

        .meta-summary {
          display: block;
          margin-top: 12px;
          padding: 8px 12px;
          border: 1px solid #e4e7ec;
          border-radius: 6px;
          background: #f8fafc;
          color: #667085;
          font-size: 12px;
          line-height: 18px;
          white-space: normal;
          overflow-wrap: anywhere;
        }

        .result-footer {
          margin-top: 20px;
          display: flex;
          justify-content: center;

          .reset-link {
            font-size: 13px;
            color: #1890ff;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }
        }
      }

      .personal-loading-section {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 48px 0;
      }

      .code-display {
        display: flex;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        overflow: hidden;
        min-height: 40px;

        input {
          flex: 1;
          min-width: 0;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 15px;
          border: none;
          border-radius: 0;
          outline: none;
          padding: 8px 12px;

          &:focus {
            box-shadow: none;
            border: none;
          }
        }

        .copy-button {
          border: none;
          border-radius: 0;
          background: #f8fafc;
          color: #344054;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 8px 16px;
          height: auto;

          box-shadow: none !important;
          outline: none !important;

          &:hover,
          &:focus,
          &:active {
            background: #eef4ff;
            border-color: #eef4ff;
            color: #0958d9;
            box-shadow: none !important;
            outline: none !important;
          }

          span[nz-icon],
          i[nz-icon] {
            font-size: 14px;
            color: currentColor;
          }
        }
      }

      .share-section {
        margin-top: 16px;
        .share-options {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
      }

      .share-button {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
        min-width: 64px;
        min-height: 64px;
        padding: 6px 8px;
        border: 1px solid transparent;
        border-radius: 6px;
        height: 60px;
        background: white;
        color: #666;
        font-size: 12px;

        img {
          width: 36px;
          height: 36px;
        }

        &:hover {
          border-color: #d6e4ff;
          background: #f5f9ff;
          color: #1890ff;
        }

        &.line {
          &:hover {
            border-color: #00b900;
            color: #00b900;
          }
        }

        &.slack {
          &:hover {
            border-color: #e01e5a;
            color: #e01e5a;
          }
        }

        &.teams {
          &:hover {
            border-color: #6264a7;
            color: #6264a7;
          }
        }

        &.gchat {
          &:hover {
            border-color: #1a73e8;
            color: #1a73e8;
          }
        }
      }

      @media (max-width: 576px) {
        .policy-card {
          min-height: auto;
        }

        .action-footer {
          .generate-button {
            width: 100%;
          }
        }

        .code-display {
          flex-direction: column;
          height: auto;

          .copy-button {
            justify-content: center;
            min-height: 40px;
            border-top: 1px solid #e4e7ec;
          }
        }
      }
    `
  ]
})
export class InviteCodeModalComponent implements OnInit {
  inviteCode: string = '';
  mobileAccessEnabled: boolean = true;
  expireDays: number = 1;
  maxUsageCount: number = 1;
  isGenerating: boolean = false;
  invitationData?: TenantInvitationVO;

  tenantId: string = '';
  isPersonalTenant: boolean = false;
  bossSnid?: string;
  hasClientAccessControlCapability: boolean = true;

  private readonly nzModalData: InviteModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    if (this.nzModalData) {
      this.inviteCode = this.nzModalData.inviteCode ?? '';
      this.tenantId = this.nzModalData.tenantId ?? '';
      this.isPersonalTenant = !!this.nzModalData.isPersonalTenant;
      this.bossSnid = this.nzModalData.bossSnid;
      this.hasClientAccessControlCapability = this.isPersonalTenant
        ? false
        : (this.nzModalData.hasDeviceNetworkAccessControlCapability ?? this.nzModalData.hasClientAccessControlCapability ?? true);
      if (!this.hasClientAccessControlCapability) {
        this.mobileAccessEnabled = true;
      }
    }
  }

  setMobileAccess(enabled: boolean): void {
    if (!this.hasClientAccessControlCapability) {
      this.mobileAccessEnabled = true;
      return;
    }
    this.mobileAccessEnabled = enabled;
  }

  generateInviteCode(): void {
    const args: any = {};
    if (this.isPersonalTenant) {
      if (!this.bossSnid) {
        this.message.error(this.i18n.fanyi('tenant.employee.business-number-not-exist'));
        return;
      }
      args.type = 'JoinServiceNumber';
      args.serviceNumberId = this.bossSnid;
      if (this.tenantId) {
        args.tenantId = this.tenantId;
      }
    } else {
      args.type = 'JoinTenant';
      args.mobileAccessEnabled = this.hasClientAccessControlCapability ? this.mobileAccessEnabled : true;
      args.expireDays = this.expireDays;
      args.maxUsageCount = this.maxUsageCount;
      if (this.tenantId) {
        args.tenantId = this.tenantId;
      }
    }

    this.isGenerating = true;
    this.http
      .post(ConstantUtil.TENANT_INVITATION_V1_CREATE, args)
      .pipe(
        finalize(() => {
          this.isGenerating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success && res.data) {
            if (res.data.warnMessage) {
              this.message.warning(res.data.warnMessage);
            }
            this.invitationData = res.data;
            this.inviteCode = res.data.code;
            if (res.data.mobileAccessEnabled !== undefined) {
              this.mobileAccessEnabled = res.data.mobileAccessEnabled;
            }
            if (res.data.expireDays !== undefined) {
              this.expireDays = res.data.expireDays;
            }
            if (res.data.maxUsageCount !== undefined) {
              this.maxUsageCount = res.data.maxUsageCount;
            }
          } else {
            showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.employee.operation-failed'));
          }
        },
        error: err => {
          showFallbackHttpError(this.message, err?.error?.msg, this.i18n.fanyi('tenant.employee.operation-failed'));
        }
      });
  }

  resetConfig(): void {
    this.inviteCode = '';
    this.invitationData = undefined;
    this.cdr.detectChanges();
  }

  getMetaSummary(): string {
    const expiryLabel = this.getExpiryLabel(this.expireDays);
    const usesLabel = this.getUsageLabel(this.maxUsageCount);
    const policyLabel =
      this.mobileAccessEnabled !== false
        ? this.i18n.fanyi('tenant.invite.meta-allow-app')
        : this.i18n.fanyi('tenant.invite.meta-desktop-only');

    return this.i18n.fanyi('tenant.invite.meta-summary', {
      expiry: expiryLabel,
      uses: usesLabel,
      policy: policyLabel
    });
  }

  private getExpiryLabel(days: number): string {
    if (days === 7) {
      return this.i18n.fanyi('tenant.invite.validity-7-days');
    }
    if (days === 30) {
      return this.i18n.fanyi('tenant.invite.validity-30-days');
    }
    return this.i18n.fanyi('tenant.invite.validity-1-day');
  }

  private getUsageLabel(count: number): string {
    if (count === 5) {
      return this.i18n.fanyi('tenant.invite.usage-5-times');
    }
    if (count === -1) {
      return this.i18n.fanyi('tenant.invite.usage-unlimited');
    }
    return this.i18n.fanyi('tenant.invite.usage-1-time');
  }

  copyInviteCode(): void {
    const textArea = document.createElement('textarea');
    const message = this.getShareMessage();
    textArea.value = message;
    document.body.appendChild(textArea);
    textArea.select();
    navigator.clipboard.writeText(message);
    this.message.success(this.i18n.fanyi('tenant.invite.invite-code-copied'));
    document.body.removeChild(textArea);
  }

  shareVia(platform: 'line' | 'slack' | 'teams' | 'gchat'): void {
    const message = this.getShareMessage();

    switch (platform) {
      case 'line':
        window.open(`https://line.me/R/msg/text/?${encodeURIComponent(message)}`);
        break;
      case 'slack':
        const slackUrl = `slack://channel?team=&id=&message=${encodeURIComponent(message)}`;
        window.open(slackUrl);
        navigator.clipboard.writeText(message).then(() => {
          this.message.info(this.i18n.fanyi('tenant.invite.slack-message-copied'));
        });
        break;
      case 'teams':
        const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?users=&message=${encodeURIComponent(message)}`;
        window.open(teamsUrl);
        navigator.clipboard.writeText(message).then(() => {
          this.message.info(this.i18n.fanyi('tenant.invite.teams-message-copied'));
        });
        break;
      case 'gchat':
        const gchatUrl = `https://chat.google.com/`;
        window.open(gchatUrl);
        navigator.clipboard.writeText(message).then(() => {
          this.message.info(this.i18n.fanyi('tenant.invite.gchat-message-copied'));
        });
        break;
    }
  }

  getShareMessage(): string {
    if (!this.isPersonalTenant && this.mobileAccessEnabled === false) {
      return this.i18n.fanyi('tenant.invite.share-message-desktop', { inviteCode: this.inviteCode });
    }
    return this.i18n.fanyi('tenant.invite.share-message', { inviteCode: this.inviteCode });
  }
}
